import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Loader2, X } from 'lucide-react';
import { components } from 'react-select';
import { Select } from '../../../components/common/Select.js';
import type { OptionLabel } from '../../../types/options.ts';
import { Tooltip } from '../../../admin/components/shared/Tooltip.tsx';
import { truncateProcessDisplayName, LINE_CLAMP_2_CLASS } from '../utils/riskDetailsUtils.ts';

/**
 * Append footer without duplicating `children`, and detect near-end scroll (react-select’s
 * `onMenuScrollToBottom` can miss with portaled/fixed menus — this keeps infinite loading reliable).
 */
const ProcessMenuList = (props: any) => {
  const lastNearEndAtRef = useRef(0);
  const menuNodeRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { children, innerProps = {}, innerRef, ...rest } = props;
  const { onScroll: innerOnScroll, ...innerRest } = innerProps;
  /** Flatten one level: our `Select` passes `selectProps={...}`; RS may also nest `selectProps.selectProps`. */
  const rawSp = rest.selectProps;
  const sp =
    rawSp && typeof rawSp === 'object'
      ? { ...rawSp, ...(rawSp.selectProps && typeof rawSp.selectProps === 'object' ? rawSp.selectProps : {}) }
      : {};
  const loadingMore = Boolean(sp.loadingMoreOptions);
  const hasMore = Boolean(sp.hasMoreOptions);
  const onNearEnd = sp.onOptionsNearEnd as (() => void) | undefined;

  const composedInnerRef = useCallback(
    (node: HTMLDivElement | null) => {
      menuNodeRef.current = node;
      if (typeof innerRef === 'function') {
        innerRef(node);
      } else if (innerRef && typeof innerRef === 'object' && 'current' in innerRef) {
        (innerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [innerRef]
  );

  const fireNearEnd = useCallback(() => {
    if (!hasMore || loadingMore || typeof onNearEnd !== 'function') return;
    const now = Date.now();
    if (now - lastNearEndAtRef.current < 350) return;
    lastNearEndAtRef.current = now;
    onNearEnd();
  }, [hasMore, loadingMore, onNearEnd]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    innerOnScroll?.(e);
    const el = e.currentTarget;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= 48) fireNearEnd();
  };

  /** Deps omit `children`: option list identity changes every render and would churn the observer. */
  useEffect(() => {
    const root = menuNodeRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((en) => en.isIntersecting)) return;
        fireNearEnd();
      },
      { root, rootMargin: '0px 0px 80px 0px', threshold: 0 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loadingMore, fireNearEnd]);

  return (
    <components.MenuList {...rest} innerRef={composedInnerRef} innerProps={{ ...innerRest, onScroll: handleScroll }}>
      {children}
      <div ref={sentinelRef} className="h-1 w-full shrink-0" aria-hidden />
      {loadingMore ? (
        <div className="flex items-center justify-center border-t border-slate-100 px-3 py-2">
          <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
        </div>
      ) : null}
    </components.MenuList>
  );
};

const PROCESS_SELECT_COMPONENTS = { MenuList: ProcessMenuList };

type OptionComponentProps = React.ComponentProps<typeof components.Option>;

function createClampedOption() {
  return function ClampedOption(optionProps: OptionComponentProps) {
    const label = String(optionProps.label ?? '');
    return (
      <components.Option {...optionProps} className="app-select__option--clamped">
        <Tooltip content={label} side="right">
          <div className="w-full min-w-0">
            <span className={`text-sm ${LINE_CLAMP_2_CLASS}`}>{label}</span>
          </div>
        </Tooltip>
      </components.Option>
    );
  };
}

const PROCESS_ASSOCIATION_SELECT_STYLES = {
  placeholder: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
    ...base,
    fontWeight: 600,
    color: state.isFocused ? '#9ca3af' : 'black',
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 70,
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 70,
  }),
  /** Ensures the menu scrolls so `onMenuScrollToBottom` can fire for infinite loading. */
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    maxHeight: 280,
    overflowY: 'auto',
  }),
};

interface RiskProcessAssociationsProps {
  selectedValues: string[];
  processOptions: OptionLabel[];
  selectedLabelsByValue?: Record<string, string>;
  /**
   * When true, only the add-process control is shown (no duplicate list).
   * Use when each linked process is shown in a row below (risk detail editor).
   */
  pickerOnly?: boolean;
  /** Controls copy when this picker is used for risks (e.g. control details) vs processes. */
  variant?: 'process' | 'risk';
  /** When true, constrain the linked-process list height (e.g. bulk modal). */
  fixedTagContainer?: boolean;
  /** How selected processes render below the picker (`pills` matches risk details / design). */
  selectedDisplay?: 'pills' | 'list';
  loadingOptions?: boolean;
  loadingMoreOptions?: boolean;
  searchInputValue?: string;
  hasMoreOptions?: boolean;
  onSearchChange?: (term: string) => void;
  onLoadMore?: () => void;
  readOnly?: boolean;
  /** Custom placeholder text when input is focused (default: "Type something to search") */
  placeholderOnFocus?: string;
  /** Custom placeholder text when input is not focused (default: "+ Add process...") */
  placeholderOnBlur?: string;
  /** When set, dropdown options clamp to this many lines with full label in a tooltip. */
  optionLineClamp?: number;
  /** Override default "Linked processes" heading (hidden when pickerOnly). */
  sectionTitle?: string;
  /** Override default helper text under the heading (hidden when pickerOnly). */
  sectionDescription?: string;
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
}

function resolveProcessLabel(
  id: string,
  processOptions: OptionLabel[],
  selectedLabelsByValue: Record<string, string>,
  readOnly = false
): string {
  if (selectedLabelsByValue[id]) return selectedLabelsByValue[id];
  if (readOnly) return id;
  return processOptions.find((o) => o.value === id)?.label ?? id;
}

export const RiskProcessAssociations: React.FC<RiskProcessAssociationsProps> = ({
  selectedValues,
  processOptions,
  selectedLabelsByValue = {},
  pickerOnly = false,
  variant = 'process',
  fixedTagContainer = false,
  selectedDisplay = 'pills',
  loadingOptions = false,
  loadingMoreOptions = false,
  searchInputValue = '',
  hasMoreOptions = false,
  onSearchChange,
  onLoadMore,
  readOnly = false,
  placeholderOnFocus = 'Type something to search',
  placeholderOnBlur = '+ Add process...',
  optionLineClamp,
  sectionTitle,
  sectionDescription,
  onAdd,
  onRemove,
}) => {
  const isRiskVariant = variant === 'risk';
  const entityPlural = isRiskVariant ? 'risks' : 'processes';
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const remainingOptions = useMemo(
    () => processOptions.filter((opt) => !selectedSet.has(opt.value)),
    [processOptions, selectedSet]
  );
  const selectOptions = useMemo(
    () => remainingOptions.map((option) => ({ value: option.value, label: option.label })),
    [remainingOptions]
  );
  const noOptionsMessage = useMemo(
    () => () => (loadingOptions ? `Loading ${entityPlural}...` : `No ${entityPlural} found`),
    [entityPlural, loadingOptions]
  );

  const processSelectProps = useMemo(
    () => ({
      loadingMoreOptions,
      hasMoreOptions,
      onOptionsNearEnd: () => {
        if (hasMoreOptions && !loadingMoreOptions) onLoadMore?.();
      },
    }),
    [hasMoreOptions, loadingMoreOptions, onLoadMore]
  );

  const selectComponents = useMemo(() => {
    if (!optionLineClamp) return PROCESS_SELECT_COMPONENTS;
    return {
      ...PROCESS_SELECT_COMPONENTS,
      Option: createClampedOption(),
    };
  }, [optionLineClamp]);

  const selectStyles = useMemo(() => {
    if (!optionLineClamp) return PROCESS_ASSOCIATION_SELECT_STYLES;
    return {
      ...PROCESS_ASSOCIATION_SELECT_STYLES,
      option: (base: Record<string, unknown>) => ({
        ...base,
        whiteSpace: 'normal',
        overflow: 'hidden',
      }),
    };
  }, [optionLineClamp]);

  const listWrapperClass =
    selectedDisplay === 'pills'
      ? fixedTagContainer
        ? 'flex max-h-40 min-h-40 flex-wrap content-start gap-2 overflow-y-auto rounded-md border border-slate-200 bg-white p-2'
        : 'flex flex-wrap gap-2 rounded-md border border-slate-200 bg-white p-2'
      : fixedTagContainer
        ? 'max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white'
        : 'rounded-md border border-slate-200 bg-white';

  const isServerSideSearch = typeof onSearchChange === 'function';

  return (
    <div className="space-y-2">
        {!pickerOnly ? (
          <>
            <p className="text-xs font-semibold text-slate-700">
              {sectionTitle ?? 'Linked processes'}
            </p>
            <p className="text-xs text-slate-500">
              {sectionDescription ?? 'Add all processes that should reference this risk.'}
            </p>
          </>
        ) : null}
        {!readOnly ? (
          <div className={pickerOnly ? 'w-full md:w-1/3 md:min-w-[11rem]' : 'w-full'}>
            <Select
              value=""
              onChange={(value: any) => {
                if (!value) return;
                onAdd(value);
              }}
              isSearchable
              inputValue={searchInputValue}
              onInputChange={(value: any, meta: any) => {
                if (meta.action === 'input-change') onSearchChange?.(value);
              }}
              onMenuClose={() => {
                onSearchChange?.('');
              }}
              onMenuScrollToBottom={() => {
                if (hasMoreOptions && !loadingMoreOptions) onLoadMore?.();
              }}
              noOptionsMessage={noOptionsMessage}
              filterOption={isServerSideSearch ? () => true : undefined}
              placeholder={placeholderOnBlur}
              placeholderOnFocus={placeholderOnFocus}
              placeholderOnBlur={placeholderOnBlur}
              components={selectComponents}
              {...processSelectProps}
              selectProps={processSelectProps}
              styles={selectStyles}
              options={selectOptions}
              menuPlacement="auto"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              menuPosition="absolute"
              className="w-full min-w-0"
              aria-label={isRiskVariant ? 'Add linked risk' : 'Add linked process'}
            />
          </div>
        ) : null}
        {!pickerOnly && selectedValues.length > 0 ? (
          selectedDisplay === 'pills' ? (
            <div className={listWrapperClass} role="list">
              {selectedValues.map((id) => {
                const label = resolveProcessLabel(id, processOptions, selectedLabelsByValue, readOnly);
                const labelDisplay = truncateProcessDisplayName(label, 40);
                return (
                  <Tooltip key={id} content={label}>
                    <span
                      role="listitem"
                      className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md border border-indigo-200/90 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-900"
                    >
                      <span className="truncate">{labelDisplay}</span>
                      {!readOnly ? (
                        <button
                          type="button"
                          onClick={() => onRemove(id)}
                          className="shrink-0 rounded-sm p-0.5 text-indigo-700 hover:bg-indigo-100/80"
                          aria-label={`Remove ${label}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            <ul className={`divide-y divide-slate-100 ${listWrapperClass}`}>
              {selectedValues.map((id) => {
                const label = resolveProcessLabel(id, processOptions, selectedLabelsByValue, readOnly);
                const labelDisplay = truncateProcessDisplayName(label, 40);
                return (
                  <li key={id} className="flex items-center justify-between gap-3 px-3 py-2.5 min-w-0">
                    <Tooltip content={label}>
                      <span className="text-xs text-slate-800 truncate min-w-0">{labelDisplay}</span>
                    </Tooltip>
                    {!readOnly ? (
                      <button
                        type="button"
                        onClick={() => onRemove(id)}
                        className="flex-shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        aria-label={`Remove ${label}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )
        ) : null}
    </div>
  );
};
